/* Onvela-configurator.
   Stuurt de live SVG-preview, de tellers en de line item properties.
   Bij verzenden van het productformulier wordt de preview als afbeelding in
   properties[_mockup] gezet. Dat is de productiespec voor de supplier:
   SKU-spec + personalisatie + mockupbeeld per order.

   Deze module doet bewust géén cart-logica: de velden staan in Dawn's eigen
   productformulier, dus toevoegen aan winkelwagen blijft native. */

class OnvelaConfigurator extends HTMLElement {
  connectedCallback() {
    this.uid = this.dataset.uid;
    this.svg = this.querySelector('.onvela-plate__svg');
    this.plate = this.querySelector('.onvela-plate');
    if (!this.svg) return;

    this.el = (name) => this.svg.querySelector(`[data-el="${name}"]`);
    this.inputs = {
      number: this.querySelector('[data-role="number"]'),
      line1: this.querySelector('[data-role="line1"]'),
      line2: this.querySelector('[data-role="line2"]'),
    };
    // LET OP: niet 'hidden' noemen. Dat is een bestaande boolean-eigenschap op
    // HTMLElement; een object toewijzen wordt stil naar true gecoerced, waarna
    // elke guard erop faalt zonder foutmelding.
    this.fields = {
      font: this.querySelector('[data-role="font-value"]'),
      mount: this.querySelector('[data-role="mount-value"]'),
      mockup: this.querySelector('[data-role="mockup"]'),
      config: this.querySelector('[data-role="config"]'),
    };

    // Limieten komen per product uit metafields en zijn exact wat de
    // leverancier toestaat; geen veilige marge.
    this.max = {
      number: parseInt(this.dataset.maxNumber || '0', 10),
      line1: parseInt(this.dataset.maxLine1 || '0', 10),
      line2: parseInt(this.dataset.maxLine2 || '0', 10),
    };

    this.loadColorMap();
    this.bindUpsell();
    this.bindText();
    this.bindOptions();
    this.bindScenes();
    this.bindForm();
    this.bindVariant();

    // Nummer uit de mini-configurator op de homepage (?nr=12) overnemen.
    const preset = new URLSearchParams(location.search).get('nr');
    if (preset && this.inputs.number) {
      this.inputs.number.value = preset.slice(0, this.max.number);
    }

    this.applyActive('font');
    this.applyActive('mount');
    this.syncVariantColor();
    this.render();
  }

  disconnectedCallback() {
    // Dawn hervormt product-info bij elke variantwissel; zonder dit blijven
    // de document-listeners van elk vorig exemplaar actief.
    this.controller?.abort();
  }

  /* ---------- Tekstvelden ---------- */
  bindText() {
    Object.entries(this.inputs).forEach(([role, input]) => {
      if (!input) return;
      input.addEventListener('input', () => {
        this.updateCount(role, input);
        this.render();
      });
      this.updateCount(role, input);
    });
  }

  updateCount(role, input) {
    const counter = this.querySelector(`[data-count="${role}"]`);
    if (!counter) return;
    counter.textContent = `${input.value.length}/${this.max[role] || 0}`;
  }

  /* ---------- Lettertype en kleur ---------- */
  bindOptions() {
    this.querySelectorAll('[data-role="font"], [data-role="mount"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const role = btn.dataset.role;
        this.querySelectorAll(`[data-role="${role}"]`).forEach((el) => {
          el.classList.toggle('is-on', el === btn);
          el.setAttribute('aria-checked', el === btn ? 'true' : 'false');
        });
        this.applyActive(role);
        this.render();
      });
    });
  }

  applyActive(role) {
    const active = this.querySelector(`[data-role="${role}"].is-on`);
    if (!active) return;

    if (role === 'font') {
      this.font = active.dataset.family;
      if (this.fields.font) this.fields.font.value = active.dataset.value;
    }

    if (role === 'mount' && this.fields.mount) {
      this.fields.mount.value = active.dataset.value;
    }
  }

  /* ---------- Kleur volgt de gekozen variant ----------
     Kleur is bij ons een variantoptie, geen configurator-keuze. De preview
     leest de geselecteerde variant en zoekt de plaat- en tekstkleur op in de
     mapping uit het colors-metafield. */
  loadColorMap() {
    const el = this.querySelector('[data-role="color-map"]');
    if (!el) return;
    try {
      this.colorMap = JSON.parse(el.textContent);
    } catch (e) {
      this.colorMap = null;
    }
  }

  bindVariant() {
    if (!this.colorMap) return;

    // Dawn's variant-radio's staan BUITEN het productformulier, dus luisteren
    // op het formulier alleen is niet genoeg; we luisteren op document-niveau.
    //
    // LET OP: Dawn hervormt product-info bij elke variantwissel, waardoor dit
    // element opnieuw wordt aangemaakt en connectedCallback opnieuw draait.
    // Zonder AbortController stapelen de listeners zich per wissel op. Een
    // MutationObserver op product-info staat hier bewust NIET: render() wijzigt
    // attributen binnen datzelfde element, dus dat is een lus die de pagina
    // laat vastlopen.
    this.controller = new AbortController();
    const opts = { signal: this.controller.signal };

    document.addEventListener('change', (e) => {
      if (e.target && e.target.matches('input[type="radio"], select')) this.syncVariantColor();
    }, opts);
    document.addEventListener('variant:change', () => this.syncVariantColor(), opts);
  }

  syncVariantColor() {
    if (!this.colorMap) return;
    const names = Object.keys(this.colorMap);
    // Alle geselecteerde optiewaarden verzamelen, ongeacht of Dawn radio's
    // of selects rendert. Bewust op document-niveau: de variant picker staat
    // buiten het productformulier.
    const picked = [
      ...document.querySelectorAll('input[type="radio"]:checked, select option:checked'),
    ].map((el) => (el.value || el.textContent || '').trim());

    let match = picked.find((v) => names.includes(v));

    // Sinds de kleursplitsing kiest de klant plaat en tekst apart ("Biała" +
    // "czarny"), terwijl het colors-metafield nog op de combinatie sleutelt
    // ("Biała + czarny"). Dat metafield blijft bewust de bron: de tekstkleur
    // hangt af van de plaat — goud op wit is een andere hex dan goud op zwart —
    // en die nuance verlies je met twee losse lijstjes. Dus stellen we de
    // sleutel hier samen. Producten met één gecombineerde optie matchen
    // hierboven al direct, dus beide vormen blijven werken.
    if (!match) {
      outer: for (const a of picked) {
        for (const b of picked) {
          if (a === b) continue;
          if (names.includes(`${a} + ${b}`)) {
            match = `${a} + ${b}`;
            break outer;
          }
        }
      }
    }

    if (!match) return;

    const entry = this.colorMap[match];
    if (!entry) return;
    this.plateColor = entry.plate;
    this.textColor = entry.text;
    this.render();
  }

  /* ---------- Scènes ---------- */
  bindScenes() {
    this.querySelectorAll('.onvela-plate__scene').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.querySelectorAll('.onvela-plate__scene').forEach((b) => {
          b.classList.toggle('is-on', b === btn);
          b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
        });
        this.setScene(btn.dataset.scene);
      });
    });
  }

  setScene(scene) {
    const lit = this.plate.dataset.lit !== 'false';
    const sky = this.el('sky');
    const wall = this.el('wall');
    const halo = this.el('halo');
    const glow = this.el('number-glow');
    const dims = this.el('dims');

    const day = scene === 'day' || scene === 'dims';
    sky.setAttribute('fill', `url(#onvSky${day ? 'Day' : 'Dusk'}-${this.uid})`);
    wall.setAttribute('fill', `url(#onvWall${day ? 'Day' : ''}-${this.uid})`);

    const showGlow = !day && lit;
    halo.setAttribute('visibility', showGlow ? 'visible' : 'hidden');
    glow.setAttribute('visibility', showGlow ? 'visible' : 'hidden');
    dims.setAttribute('visibility', scene === 'dims' ? 'visible' : 'hidden');

    this.svg.setAttribute('viewBox', scene === 'detail' ? '200 160 420 360' : '0 0 800 680');
    this.plate.dataset.scene = scene;
  }

  /* ---------- Render ---------- */
  render() {
    const number = (this.inputs.number?.value || '').trim();
    const line1 = (this.inputs.line1?.value || '').trim().toUpperCase();
    const line2 = (this.inputs.line2?.value || '').trim().toUpperCase();

    const numEl = this.el('number');
    const glowEl = this.el('number-glow');
    const streetEl = this.el('street');
    const ruleEl = this.el('rule');

    // Lettergrootte schaalt mee met de lengte, zoals in de mockup.
    const size = number.length <= 2 ? 120 : number.length === 3 ? 96 : 78;

    [numEl, glowEl].forEach((el) => {
      el.textContent = number;
      el.setAttribute('font-size', size);
      el.setAttribute('fill', el === glowEl ? '#F5C87A' : this.textColor || '#F8EED9');
      if (this.font) el.setAttribute('font-family', this.font);
    });

    // Regel 2 hangt onder regel 1; staat er maar één, dan gebruikt die de
    // bovenste positie zodat het bord niet scheef oogt.
    const lines = [line1, line2].filter(Boolean);
    streetEl.textContent = lines.join('  ·  ');
    streetEl.setAttribute('visibility', lines.length ? 'visible' : 'hidden');
    streetEl.setAttribute('fill', this.textColor === '#14181d' ? '#3a4048' : '#EDE4D2');
    if (this.font) streetEl.setAttribute('font-family', this.font);
    ruleEl.setAttribute('visibility', lines.length ? 'visible' : 'hidden');

    if (this.plateColor) this.el('plate').setAttribute('fill', this.plateColor);

    if (this.fields.config) {
      this.fields.config.value = JSON.stringify({
        number,
        line1,
        line2,
        font: this.fields.font?.value || null,
        mount: this.fields.mount?.value || null,
        plate: this.plateColor || null,
      });
    }
  }

  /* ---------- Upsell ----------
     Het accessoire is een eigen product, dus het gaat als tweede regel in de
     winkelwagen. We onderscheppen de klik op de koopknop, leggen het accessoire
     eerst neer, en laten daarna Dawn zijn eigen toevoeging afhandelen. Zo
     hoeven we niets van Dawn's cart-logica over te nemen. */
  bindUpsell() {
    this.upsell = this.querySelector('[data-role="upsell"]');
    if (!this.upsell) return;

    const included = this.querySelector('[data-role="included"]');
    this.upsell.addEventListener('change', () => {
      if (!included) return;
      const on = this.upsell.checked;
      included.querySelector('[data-included="off"]').hidden = on;
      included.querySelector('[data-included="on"]').hidden = !on;
    });

    const btn = document.querySelector('button[name="add"], .product-form__submit');
    if (!btn) return;

    btn.addEventListener('click', async (e) => {
      if (!this.upsell.checked || this.upsellPending) return;
      const id = this.upsell.dataset.variantId;
      if (!id) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      this.upsellPending = true;
      btn.setAttribute('aria-busy', 'true');

      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
        });
      } catch (err) {
        // Mislukt het accessoire, dan gaat het bord alsnog in de winkelwagen.
        // De property legt de keuze vast, dus de order blijft afhandelbaar.
      }

      btn.removeAttribute('aria-busy');
      btn.click();
      this.upsellPending = false;
    }, { capture: true });
  }

  /* ---------- Productiespec bij toevoegen aan winkelwagen ---------- */
  bindForm() {
    // De configurator staat visueel bóven de knop en dus buiten Dawn's
    // formulier. De velden hangen eraan via het form-attribuut; hier zoeken
    // we hetzelfde formulier op om de mockup te kunnen exporteren.
    this.form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
    if (!this.form) return;
    this.form.addEventListener('submit', () => this.exportMockup(), { capture: true });
  }

  exportMockup() {
    if (!this.fields.mockup) return;
    try {
      const clone = this.svg.cloneNode(true);
      clone.setAttribute('viewBox', '200 160 420 360');
      clone.setAttribute('width', 840);
      clone.setAttribute('height', 720);
      const xml = new XMLSerializer().serializeToString(clone);
      // Alleen de bronmarkup meesturen; de orderflow zet dit om naar een
      // afbeelding voor de productiespec.
      this.fields.mockup.value = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(xml)));
    } catch (e) {
      // Mislukt de export, dan gaat de bestelling gewoon door; de tekstuele
      // properties bevatten alles wat de productiespec nodig heeft.
      this.fields.mockup.value = '';
    }
  }
}

if (!customElements.get('onvela-configurator')) {
  customElements.define('onvela-configurator', OnvelaConfigurator);
}
